import React, { createContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { Address, DeliveryOption } from '../types';
import { useAuth } from '../hooks/useAuth';
import { db } from '../config/firebase';
import { doc, getDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { User } from 'firebase/auth';

interface CheckoutState {
    selectedAddress: Address | null;
    billingAddress: Address | null;
    deliverySelections: Record<string, DeliveryOption | null>;
    appliedCoupon: string | null;
    shippingPreference: 'split' | 'consolidated';
}

interface CheckoutContextType extends CheckoutState {
    setSelectedAddress: (address: Address | null) => void;
    setBillingAddress: (address: Address | null) => void;
    setDeliverySelections: (options: Record<string, DeliveryOption | null>) => void;
    setAppliedCoupon: (coupon: string | null) => void;
    setShippingPreference: (pref: 'split' | 'consolidated') => void;
    clearCheckoutState: () => Promise<void>;
    isCheckoutLoading: boolean;
}

export const CheckoutContext = createContext<CheckoutContextType | undefined>(undefined);

export const CheckoutProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { user, loading: authLoading } = useAuth();
    const [isCheckoutLoading, setIsCheckoutLoading] = useState(true);

    const [selectedAddress, setSelectedAddress] = useState<Address | null>(null);
    const [billingAddress, setBillingAddress] = useState<Address | null>(null);
    const [deliverySelections, setDeliverySelections] = useState<Record<string, DeliveryOption | null>>({});
    const [appliedCoupon, setAppliedCoupon] = useState<string | null>(null);
    const [shippingPreference, setShippingPreference] = useState<'split' | 'consolidated'>('split');

    useEffect(() => {
        if (authLoading) return;

        const loadState = async (currentUser: User) => {
            setIsCheckoutLoading(true);
            const checkoutRef = doc(db, 'checkout_sessions', currentUser.uid);
            const docSnap = await getDoc(checkoutRef);

            if (docSnap.exists()) {
                const data = docSnap.data() as CheckoutState;
                setSelectedAddress(data.selectedAddress || null);
                setBillingAddress(data.billingAddress || null);
                setDeliverySelections(data.deliverySelections || {});
                setAppliedCoupon(data.appliedCoupon || null);
                setShippingPreference(data.shippingPreference || 'split');
            } else {
                setSelectedAddress(null);
                setBillingAddress(null);
                setDeliverySelections({});
                setAppliedCoupon(null);
                setShippingPreference('split');
            }
            setIsCheckoutLoading(false);
        };
        
        if (user) {
            loadState(user);
        } else {
            setSelectedAddress(null);
            setBillingAddress(null);
            setDeliverySelections({});
            setAppliedCoupon(null);
            setShippingPreference('split');
            setIsCheckoutLoading(false);
        }

    }, [user, authLoading]);

    useEffect(() => {
        if (isCheckoutLoading || authLoading || !user) {
            return;
        }

        const saveState = async () => {
            const stateToSave: CheckoutState = {
                selectedAddress,
                billingAddress,
                deliverySelections,
                appliedCoupon,
                shippingPreference
            };

            const isStateEmpty = !selectedAddress && !billingAddress && Object.keys(deliverySelections).length === 0 && !appliedCoupon;

            try {
                const checkoutRef = doc(db, 'checkout_sessions', user.uid);
                if (isStateEmpty) {
                    const docSnap = await getDoc(checkoutRef);
                    if (docSnap.exists()) {
                        await deleteDoc(checkoutRef);
                    }
                } else {
                    await setDoc(checkoutRef, stateToSave, { merge: true });
                }
            } catch (error) {
                console.error("Failed to save checkout state:", error);
            }
        };
        
        const timer = setTimeout(saveState, 1000);
        return () => clearTimeout(timer);

    }, [selectedAddress, billingAddress, deliverySelections, appliedCoupon, shippingPreference, user, authLoading, isCheckoutLoading]);
    
    const clearCheckoutState = useCallback(async () => {
        setSelectedAddress(null);
        setBillingAddress(null);
        setDeliverySelections({});
        setAppliedCoupon(null);
        setShippingPreference('split');
        if (user) {
            const checkoutRef = doc(db, 'checkout_sessions', user.uid);
            await deleteDoc(checkoutRef).catch(e => console.error("Error clearing checkout state from DB:", e));
        }
    }, [user]);

    const value = {
        selectedAddress,
        setSelectedAddress,
        billingAddress,
        setBillingAddress,
        deliverySelections,
        setDeliverySelections,
        appliedCoupon,
        setAppliedCoupon,
        shippingPreference,
        setShippingPreference,
        clearCheckoutState,
        isCheckoutLoading,
    };

    return (
        <CheckoutContext.Provider value={value}>
            {children}
        </CheckoutContext.Provider>
    );
};